import * as React from "react";
import { Box, Stack, Typography, Grid, Chip, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import Paper from '@mui/material/Paper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build'; // For maintenance/refactor
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// --- MODERN STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  height: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  }
});

const VersionChip = styled(Chip)({
  fontWeight: 800,
  borderRadius: "8px",
  height: "28px",
  backgroundColor: "#1E293B",
  color: "#FFFFFF",
});

const SectionHeader = styled(Typography)({
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "0.875rem",
  letterSpacing: "0.05em",
  marginBottom: "12px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

function ChangeLog() {
  const clickCountRef = React.useRef(0);

  const handleTitleClick = () => {
    const roleId = localStorage.getItem("roleId");
    if (roleId !== "af7c1fe6-d669-414e-b066-e9733f0de7a8") return;

    clickCountRef.current += 1;
    if (clickCountRef.current >= 5) {
      clickCountRef.current = 0;
      try {
        const existing = localStorage.getItem("superadmin");
        if (existing) {
          localStorage.removeItem("superadmin");
        } else {
          localStorage.setItem("superadmin", "true");
        }
        window.dispatchEvent(new Event("superadminToggled"));
      } catch (e) {
        console.error("Failed to toggle superadmin flag", e);
      }
    }
  };

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* HEADER */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#eb1d27",
              fontWeight: 800,
              letterSpacing: "2px",
              display: "block",
              mb: 1
            }}
          >
            System Updates
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2
            }}
            onClick={handleTitleClick}
          >
            Changelog
          </Typography>
          <Typography variant="body1" sx={{ color: "#64748B", maxWidth: "600px", fontSize: "1.1rem" }}>
            Follow the evolution of the Flywheel Data Studio. We're constantly improving to help you sell smarter.
          </Typography>
        </Box>

        <Stack spacing={4}>
          {/* VERSION 2.1.0 */}
          <GlassCard>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "sticky", top: "20px" }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <VersionChip label="v2.1.0" />
                    <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600 }}>
                      Latest
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                    Pending Prospects Insights
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500 }}>
                    May 2026
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#10B981" }}>
                      <CheckCircleIcon fontSize="small" /> New Features & UI
                    </SectionHeader>
                    <List dense>
                      {[
                        "Added an 'All Clear' panel above the Pending Prospects chart that highlights sales executives with zero pending prospects via green check-icon chips.",
                        "Filtered zero-pending executives out of the bar chart so red bars represent actual workload instead of empty columns.",
                        "Added a celebratory empty state when every executive is caught up across the team.",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#F59E0B" }}>
                      <BuildIcon fontSize="small" /> Maintenance & Refactor
                    </SectionHeader>
                    <List dense>
                      {[
                        "Split pending-prospects data into active vs cleared executives via memoized selectors in the dashboard feed.",
                        "Adjusted the Pending Prospects chart container to grow with content so the All Clear panel never squeezes the bars.",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </GlassCard>

          {/* VERSION 2.0.8 */}
          <GlassCard>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "sticky", top: "20px" }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <VersionChip label="v2.0.8" />
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                    Prospects, Notifications & Bug Fixes
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500 }}>
                    March 2026
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#10B981" }}>
                      <CheckCircleIcon fontSize="small" /> New Features & UI
                    </SectionHeader>
                    <List dense>
                      {[
                        "v29032606 - Introduced Prospects module with full CRUD, status management, and unresolved count badges.",
                        "v29032605 - Added Push Notifications admin page for sending targeted push messages to subscribed users.",
                        "v29032603 - Integrated browser push notification service with service worker registration and subscription management.",
                        "v29032602 - Added prospect badge count in sidebar and mobile notification bell.",
                        "v28032602 - Set up GitHub Actions CI/CD deployment pipeline for automated builds.",
                        "v29032601 - Added Prospects and Push Notifications tiles with badges in Admin Panel.",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#EF4444" }}>
                      <BugReportIcon fontSize="small" /> Bug Fixes & Improvements
                    </SectionHeader>
                    <List dense>
                      {[
                        "v28032601 - Fixed superadmin toggle not updating sidebar due to React StrictMode double-invocation.",
                        "v28032601 - Fixed profile username not capitalizing first letter on sidebar.",
                        "v29032601 - Converted PWA manifest to standalone display with proper theme colors.",
                        "v29032602 - Added prospectService and pushSubscriptionService abstractions for API operations.",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </GlassCard>

          {/* VERSION 2.0.6 */}
          <GlassCard>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "sticky", top: "20px" }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <VersionChip label="v2.0.8" />
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                    Enquiries Module & Admin Enhancements
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500 }}>
                    March 2026
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#10B981" }}>
                      <CheckCircleIcon fontSize="small" /> New Features & UI
                    </SectionHeader>
                    <List dense>
                      {[
                        "Introduced Enquiries module with a modern, card-based layout for managing incoming customer enquiries.",
                        "Added rich enquiry details parsing and chip-based display for flexible JSON payloads.",
                        "Implemented clear status chips and colored card accents for New/Pending, Contacted, and Resolved states.",
                        "Integrated real-time pending enquiry badge counts into the sidebar and Admin Panel Enquiries card.",
                        "Moved Enquiries into the Admin Console with a dedicated tile and pending-count badge."
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#F59E0B" }}>
                      <BuildIcon fontSize="small" /> Maintenance & Access Control
                    </SectionHeader>
                    <List dense>
                      {[
                        "Added enquiryService abstraction for all /api/enquiry operations (list, update status, delete).",
                        "Standardized delete permissions for Inventory, Blogs, and Enquiries so only superadmin users can delete.",
                        "Refined enquiry cards with compact layout, improved 12-hour timestamps, and modern contact header styling.",
                        "Introduced an internal superadmin toggle accessible via five clicks on the Changelog title."
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </GlassCard>

          {/* VERSION 2.0.3 */}
          <GlassCard>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "sticky", top: "20px" }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <VersionChip label="v2.0.3" />
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                    UI Modernization & Cleanup
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500 }}>
                    February 2026
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#10B981" }}>
                      <CheckCircleIcon fontSize="small" /> New Features & UI
                    </SectionHeader>
                    <List dense>
                      {[
                        "Global UI Overhaul: Aligned Closing Reasons, Reports, and Admin Panel with modern responsive design system.",
                        "Enhanced Responsiveness: Improved layout behavior on mobile and tablet devices for seamless access.",
                        "Refactored Admin Console: Simplified navigation and converted to a responsive glassmorphism grid.",
                        "Codebase Optimization: Migrated generic styles to strict Material UI system for consistency.",
                        "Standardized DataGrids: Unified table styling across Leads, Call Logs, and Closing Reasons.",
                        "Reports Module: Completely redesigned with modern charts and cleaner metrics display."
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#F59E0B" }}>
                      <BuildIcon fontSize="small" /> Maintenance & Refactor
                    </SectionHeader>
                    <List dense>
                      {[
                         "Removed deprecated 'Stock In/Out History' module to streamline the application.",
                         "Cleaned up legacy CSS files in favor of styled-components.",
                         "Fixed routing references in AppRoute.js.",
                         "Optimized layout containers (RootContainer, ContentArea) for better reusability."
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </GlassCard>

          {/* VERSION 3.0 (Legacy) */}
          <GlassCard sx={{ opacity: 0.9 }}>
             <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Box>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Chip label="v1.0.0" size="small" sx={{ fontWeight: 700, borderRadius: "6px" }} />
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", mb: 0.5 }}>
                    Major Feature Release
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500 }}>
                    September 2023
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={9}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#10B981" }}>
                      <CheckCircleIcon fontSize="small" /> Features Launched
                    </SectionHeader>
                    <List dense>
                      {[
                        "Cloned Local Database for testing",
                        "Added Open, Closed, Due follow-ups, and client info cards in the feed",
                        "Added interesting Car Trivia Facts",
                        "Added Task Reminder",
                        "Graph added for visualizing data for leads, calls, and customer visits in the feed",
                        "Added StockIn&Out Section in AdminPanel",
                        "Added Leads Closing Reasons",
                        "Improved responsiveness for a better user experience",
                        "Added ChangeLogs Page",
                        "Implemented an AdminPanel, centralizing root-level control."
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionHeader sx={{ color: "#EF4444" }}>
                      <BugReportIcon fontSize="small" /> Bug Fixes
                    </SectionHeader>
                    <List dense>
                      {[
                        "Add Customer Button replaced by edit after converting customer",
                        "While editing a customer the existing brands data was not displaying in the modal list suggestions",
                        "Disabled button after adding or updating data - for preventing duplicacy",
                        "Fixed customer convert issue just after creating a new lead",
                        "Fixed Date Picker Highlighted Red"
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5, alignItems: "flex-start" }}>
                          <ListItemIcon sx={{ minWidth: "24px", mt: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 6, color: "#CBD5E1" }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ fontSize: "0.95rem", color: "#334155", lineHeight: 1.5 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </GlassCard>

        </Stack>
      </ContentArea>
    </RootContainer>
  );
}

export default ChangeLog;
